import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Zap, Shield, Users, CheckCircle2, Target } from 'lucide-react';

export const SocialProof = () => {
  const capabilities = [
    { value: "1-click", label: "CRM updates from voice & meetings", icon: Rocket },
    { value: "6", label: "Live integrations", icon: Zap },
    { value: "0", label: "Manual entry required", icon: Shield },
    { value: "EU", label: "Data residency, GDPR-aligned", icon: Shield }
  ];

  const betaFeedback = [
    {
      quote: "Finally, I can update HubSpot without touching my laptop.",
      role: "Enterprise AE",
      context: "Beta feedback"
    },
    {
      quote: "This is what HubSpot should have built years ago.",
      role: "SDR Team Lead", 
      context: "Beta feedback"
    },
    {
      quote: "My forecast accuracy improved because reps actually update deals now.",
      role: "Sales Director",
      context: "Beta feedback"
    }
  ];

  return (
    <section className="w-full py-20 md:py-32 bg-background relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        {/* Capabilities bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-3xl bg-gradient-to-br from-primary to-primary/90 text-primary-foreground">
            {capabilities.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <item.icon className="w-6 h-6 mx-auto mb-2 opacity-80" />
                <div className="text-3xl md:text-4xl font-bold mb-1">{item.value}</div>
                <div className="text-sm text-primary-foreground/70">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
            Shaped by <span className="text-gradient-teal">real sales teams</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every feature was built from feedback by revenue leaders and sales reps. Here is what they told us.
          </p>
        </motion.div>

        {/* Beta tester feedback */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {betaFeedback.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="h-full p-8 rounded-2xl bg-card border border-border hover:border-primary/30 shadow-subtle hover:shadow-card transition-all duration-300">
                <div className="text-4xl text-primary/30 mb-4">"</div>
                <p className="text-lg text-foreground mb-6 italic">{item.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">{item.role}</div>
                    <div className="text-sm text-muted-foreground">{item.context}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};
